import { Dialog, Loader, useDialog } from 'components';
import ErrorDialog from 'components/organization/dialog/error-dialog';
import SuccessDialog from 'components/organization/dialog/success-dialog';
import { useAuth } from 'contexts';
import { useResponse } from 'contexts/response';
import { useFleetFiles } from 'hooks';
import { useCustomerAPI } from 'hooks/use-customer-api';
import useTimeZone from 'hooks/use-timezone';
import { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Customer, EdiCustomerLocation, FleetFile, Role } from 'types';
import { parseEmailList } from 'utils/format';
import { BulkResendEdiDialog } from '../bulk-resend-edi-dialog';

/*
 * Render a recipient list from either a comma-separated string (daily report `email`) or a string
 * array (`interchangeRecipients`). Blanks are dropped so legacy junk like [''] or "a,,b" shows real
 * addresses — and an empty list falls back to "None" rather than an empty/blank row.
 */
const EmailList: FC<{ value?: string | string[] | null }> = ({ value }) => {
  const emails = parseEmailList(Array.isArray(value) ? value.join(',') : value);
  return emails.length > 0 ? (
    <>
      {emails.map((email, index) => (
        <div key={index}> {email} </div>
      ))}
    </>
  ) : (
    <p>None</p>
  );
};

const Recipient: FC<EdiCustomerLocation> = ({
  ediType,
  mappingId,
  partnershipId,
  customerCode,
  locationCode,
  fileExtension,
  enabled
}) => (
  <div className="grid-7 p-2 w-100">
    <div>
      <h6 className="text-dark font-weight-regular">EDI Type</h6>
      <p className="text-black">{ediType}</p>
    </div>
    <div>
      <h6 className="text-dark font-weight-regular">Mapping ID</h6>
      <p className="text-black">{mappingId}</p>
    </div>
    <div>
      <h6 className="text-dark font-weight-regular">Partnership ID</h6>
      <p className="text-black">{partnershipId}</p>
    </div>
    <div>
      <h6 className="text-dark font-weight-regular">Customer Code</h6>
      <p className="text-black">{customerCode ?? ''}</p>
    </div>
    <div>
      <h6 className="text-dark font-weight-regular">Location Code</h6>
      <p className="text-black">{locationCode ?? ''}</p>
    </div>
    <div>
      <h6 className="text-dark font-weight-regular">File Extension</h6>
      <p className="text-black">{fileExtension}</p>
    </div>
    <div>
      <h6 className="text-dark font-weight-regular">Enabled</h6>
      <input type="checkbox" disabled checked={enabled} />
    </div>
  </div>
);

export const ViewCustomer: FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { userProfile } = useAuth();
  const { getCustomerById, updateCustomerStatus } = useCustomerAPI();
  const { getCustomerMappingById } = useFleetFiles();
  const { closeDialog, openDialog, ref } = useDialog();
  const { timeZoneIndicator, timeZoneOffset } = useTimeZone();

  const [loading, setLoading] = useState<boolean>(false);
  const { error, setError } = useResponse();

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [fleetFileMapping, setFleetFileMapping] = useState<FleetFile.Customer | null>();
  const [resendEdi, setResendEdi] = useState<boolean>(false);

  useEffect(() => {
    const customerId = Number(params.id);
    if (isNaN(customerId)) return;
    getCustomerById(customerId).then(setCustomer);
    getCustomerMappingById(customerId).then(setFleetFileMapping);
  }, [params.id, getCustomerById, getCustomerMappingById]);

  const handleCloseDialog = useCallback(async () => {
    if (!loading) {
      closeDialog();
      setError('');
    }
  }, [closeDialog, loading, setError]);

  const handleStatusChange = useCallback(async () => {
    if (!!customer) {
      setLoading(true);
      const response = await updateCustomerStatus(customer.id, !customer.deleted);
      setCustomer(response);
      if (!!response) {
        await handleCloseDialog();
      }
      setLoading(false);
    }
  }, [customer, handleCloseDialog, updateCustomerStatus]);

  return (
    <>
      <Loader loading={!customer}>
        {!!customer && (
          <div className="page" id="customer-details">
            <div className="page-header">
              <h1>CUSTOMER {customer.name && customer.code ? `${customer.name} (${customer.code})` : 'N/A'}</h1>
              <div className="d-flex g-3 flex-row-reverse">
                <button
                  className="btn-primary text-uppercase"
                  onClick={() => navigate('edit', { replace: true })}
                  data-cy="customer-edit-button"
                >
                  Edit
                </button>
                {[Role.ADMIN, Role.BILLING_ADMIN, Role.EXTERNAL].includes(userProfile.role) && (
                  <button
                    className="btn-outline text-uppercase"
                    onClick={() => setResendEdi(true)}
                    data-cy="customer-bulk-edi-button"
                  >
                    Bulk EDI
                  </button>
                )}
                {[Role.ADMIN, Role.BILLING_ADMIN].includes(userProfile.role) &&
                  (customer.deleted ? (
                    <button
                      className="btn-outline text-uppercase"
                      onClick={openDialog}
                      data-cy="customer-activate-button"
                    >
                      Activate
                    </button>
                  ) : (
                    <button
                      className="btn-danger text-uppercase"
                      onClick={openDialog}
                      data-cy="customer-deactivate-button"
                    >
                      Deactivate
                    </button>
                  ))}
                <button className="btn-back" onClick={() => navigate('/settings/customers', { replace: true })}>
                  <span className="material-icons">arrow_back_ios</span>
                  BACK
                </button>
              </div>
            </div>
            <div className="card-primary">
              <div className="card-header px-3">
                <h4 className="text-uppercase">Customer Information</h4>
              </div>
              <div className="card-body">
                <div className="grid-4 p-2">
                  <div>
                    <h6 className="text-dark font-weight-regular">Name</h6>
                    <p className="text-black">{customer.name}</p>
                  </div>
                  <div>
                    <h6 className="text-dark font-weight-regular">Code</h6>
                    <p className="text-black">{customer.code}</p>
                  </div>
                  <div>
                    <h6 className="text-dark font-weight-regular">Location</h6>
                    <p className="text-black">{customer.location.name}</p>
                  </div>
                  <div>
                    <h6 className="text-dark font-weight-regular">Status</h6>
                    <p className="text-black">{customer.deleted ? 'Deactivated' : 'Active'}</p>
                  </div>
                  <div>
                    <h6 className="text-dark font-weight-regular">Activity Report ({timeZoneIndicator})</h6>
                    <p className="text-black">
                      {!customer.activityReportHours?.length
                        ? 'None'
                        : customer.activityReportHours
                            .map(utcHour => (24 + utcHour + timeZoneOffset) % 24)
                            .sort()
                            .map(hour => `${hour % 12 || 12}${hour < 12 ? 'AM' : 'PM'}`)
                            .join(', ')}
                    </p>
                  </div>
                  <div>
                    <h6 className="text-dark font-weight-regular">Status Report ({timeZoneIndicator})</h6>
                    <p className="text-black">
                      {!customer.statusReportHours?.length
                        ? 'None'
                        : customer.statusReportHours
                            .map(utcHour => (24 + utcHour + timeZoneOffset) % 24)
                            .sort()
                            .map(hour => `${hour % 12 || 12}${hour < 12 ? 'AM' : 'PM'}`)
                            .join(', ')}
                    </p>
                  </div>
                  <div>
                    <h6 className="text-dark font-weight-regular">Inventory Report ({timeZoneIndicator})</h6>
                    <p className="text-black">
                      {!customer.inventoryReportHours?.length
                        ? 'None'
                        : customer.inventoryReportHours
                            .map(hour => (24 + hour + timeZoneOffset) % 24)
                            .sort()
                            .map(hour => `${hour % 12 || 12}${hour < 12 ? 'AM' : 'PM'}`)
                            .join(', ')}
                    </p>
                  </div>
                </div>
                <div className="grid-2 p-2">
                  <div>
                    <h6 className="text-dark font-weight-regular">Notes</h6>
                    <p className="text-black" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{customer.notes ?? ''}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="card-primary">
              <div className="card-header px-3">
                <h4 className="text-uppercase">Fleet File Account</h4>
                {[Role.ADMIN, Role.BILLING_ADMIN].includes(userProfile.role) && (
                  <button
                    className="btn-primary"
                    style={{ marginRight: -12 }}
                    onClick={() => navigate('fleet-files', { replace: true })}
                  >
                    EDIT MAPPING
                  </button>
                )}
              </div>
              <div className="card-body">
                {fleetFileMapping && (
                  <h6 className="text-dark font-weight-regular">
                    {fleetFileMapping.code}. {fleetFileMapping.name}
                  </h6>
                )}
              </div>
            </div>
            <div className="grid-auto">
              <div className="card-primary">
                <div className="card-header px-3">
                  <h4 className="text-uppercase">EDI Recpient(s)</h4>
                  {[Role.ADMIN, Role.BILLING_ADMIN].includes(userProfile.role) && (
                    <button
                      className="btn-primary"
                      style={{ marginRight: -12 }}
                      onClick={() => navigate('edi-recipients', { replace: true })}
                    >
                      EDIT EDI
                    </button>
                  )}
                </div>
                <div className="card-body">
                  <div className="d-flex flex-column align-items-start">
                    {!!customer.ediRecipients && customer.ediRecipients.length > 0 ? (
                      customer.ediRecipients.map((recipient, index) => <Recipient key={index} {...recipient} />)
                    ) : (
                      <p>None</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className='grid-auto'>
              <div className="card-primary">
                <div className="card-header px-3">
                  <h4 className="text-uppercase">Interchange Receipt Recipient(s)</h4>
                </div>
                <div className="card-body">
                  <div className="d-flex flex-column align-items-start">
                    <EmailList value={customer.interchangeRecipients} />
                  </div>
                </div>
              </div>
              <div className="card-primary">
                <div className="card-header px-3">
                  <h4 className="text-uppercase">Daily Report Recipient(s)</h4>
                </div>
                <div className="card-body">
                  <div className="d-flex flex-column align-items-start">
                    <EmailList value={customer.email} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Loader>
      <Dialog closeDialog={handleCloseDialog} ref={ref} data-cy="customer-dialog">
        {!error ? (
          <Loader loading={loading}>
            <div className="d-flex flex-column g-3">
              <h3 className="text-info-dark font-weight-medium">{`${
                customer?.deleted ? 'Activate' : 'Deactivate'
              } Customer`}</h3>
              <p className="text-info-main">{`${customer?.deleted ? 'Activate' : 'Deactivate'} customer ${
                customer?.name
              } (${customer?.code})?`}</p>
              <div className="grid-3 mt-3">
                <button className="btn-outline col-start-2" onClick={closeDialog}>
                  close
                </button>
                <button className="btn-danger col-start-3" onClick={handleStatusChange} data-cy="confirm-button">
                  confirm
                </button>
              </div>
            </div>
          </Loader>
        ) : (
          <div className="d-flex flex-column g-3">
            <h3 className="text-info-dark font-weight-medium">Updating Customer Status Failed</h3>
            <p className="text-info-main">{error}</p>
            <div className="grid-3 mt-3">
              <button className="btn-outline col-start-3" onClick={handleCloseDialog}>
                close
              </button>
            </div>
          </div>
        )}
      </Dialog>
      <ErrorDialog title="Save Customer Failed" />
      <SuccessDialog />
      <BulkResendEdiDialog open={resendEdi} handleClose={() => setResendEdi(false)} customer={customer} />
    </>
  );
};
